import { StyledTextField } from "components/inputs/StyledTextField.tsx"
import { OreveButton } from "components/button/OreveButton.tsx"
import {
    Control,
    FieldErrors,
    FieldValues,
    UseFormClearErrors,
    UseFormSetError,
    UseFormGetValues,
    UseFormSetValue,
    Controller,
    FieldPath,
    PathValue,
} from "react-hook-form"
import React from "react"
import { Typography, useTheme, Popover, Stack, Switch, IconButton } from "@mui/material"
import { Box } from "@mui/system"
import ClearIcon from "@mui/icons-material/Clear"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InfoOutline from "@mui/icons-material/InfoOutline"
import Socket from "assets/svg/socket.svg"
import { ConfirmedVids } from "types/list-card-holder"

type AutochargeVidSectionProps<FormT extends FieldValues = FieldValues> = {
    control: Control<FormT>
    errors: FieldErrors<FormT>
    setError: UseFormSetError<FormT>
    clearErrors: UseFormClearErrors<FormT>
    getValues: UseFormGetValues<FormT>
    setValue: UseFormSetValue<FormT>
    confirmedVids: ConfirmedVids
    setConfirmedVids: React.Dispatch<React.SetStateAction<ConfirmedVids>>
    max?: number
    index?: number
    cancelDelete?: boolean
    isUpdateMode?: boolean
    isVehicleInactive?: boolean
    initialConfirmedVids?: ConfirmedVids
}

export const AutochargeVidSection = <FormT extends FieldValues>({
  control,
  errors,
  setError,
  clearErrors,
  getValues,
  setValue,
  confirmedVids,
  setConfirmedVids,
  max = 4,
  index = 0,
  cancelDelete = false,
  isUpdateMode = false,
  isVehicleInactive = false,
  initialConfirmedVids,
}: AutochargeVidSectionProps<FormT>) => {
    const theme = useTheme()

    const confirmedCount = confirmedVids.length

    const uidName = `vids.${index}.uid` as const
    const uidConfirmName = `vids.${index}.uidConfirm` as const

    type VidsErrorsShape = {
        vids?: Array<{
            uid?: { message?: string }
            uidConfirm?: { message?: string }
        }>
    }
    const vidsErrors = errors as unknown as VidsErrorsShape

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const lockAutochargeEdition = isUpdateMode && isVehicleInactive

    const initialVids = React.useMemo(
        () => new Set((initialConfirmedVids ?? []).map(({ vid }) => vid)),
        [initialConfirmedVids]
    )

    return (
      <Box
          sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
          }}
      >
          <Box
              sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
              }}
          >
              <Typography variant="headlineSmall" component="div">
                  <Box
                      component="img"
                      src={Socket}
                      alt="oreve-socket-icon"
                      sx={{ mr: 1.5, width: "1.3rem", verticalAlign: "bottom" }}
                  />
                  <Box component="span" sx={{
                      color: "text.primary" ,
                  }}>
                      Configuration de l'autocharge
                  </Box>
              </Typography>
              <Typography
                  color="black"
                  variant="labelLarge"
                  component="div"
                  align={"left"}
                  fontWeight={400}
                  marginTop={"1.5rem"}
              >
                  {" "}Pour activer l'autocharge, veuillez renseigner le ou les Vehicle IDentifier (4 VID max par véhicules). Vous pouvez activer ou désactiver l'Autocharge pour chaque VID à tout moment.
                  <Typography
                      aria-owns={open ? 'mouse-over-popover' : undefined}
                      aria-haspopup="true"
                      onMouseEnter={handlePopoverOpen}
                      onMouseLeave={handlePopoverClose}
                      sx={{margin: 0, padding: 0, width: "20px", display: "inline-block"}}
                  >
                      <InfoOutline sx={{color: "#0062FF", fontSize: "1.15rem", verticalAlign: "sub"}} />
                  </Typography>
                  <Popover
                      id="mouse-over-popover"
                      sx={{ pointerEvents: 'none' }}
                      open={open}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                      }}
                      transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                      }}
                      onClose={handlePopoverClose}
                      disableRestoreFocus
                      slotProps={{
                          paper: {
                              sx: {
                                  borderRadius: "13px",
                                  marginTop: "-60px",
                                  boxShadow: "0px 10px 16px -4px rgba(0,0,0,.6)"
                              },
                          },
                      }}
                  >
                      <Typography sx={{ p: 1, background: "#E2EFFF", fontSize: "0.75rem" }}>
                          Pour obtenir le ou les VID, rapprochez vous du constructeur <br />
                          ou consultez les documents officiels du véhicule.
                      </Typography>
                  </Popover>
              </Typography>

              <Stack spacing={2} sx={{ my: "0.75rem", pointerEvents: cancelDelete ? "none" : "auto" }}>
                  {confirmedVids.map(({ vid, enabled }, i) => {
                      const isNewVid = !initialVids.has(vid)
                      const showClearButton =
                          !isUpdateMode || isUpdateMode && !initialVids.has(vid)

                      return (
                          <Box
                              key={`vid-row-${vid}`}
                              sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                              }}
                          >
                              <Box
                                  key={`vid-row-${vid}`}
                                  sx={{
                                      flex: 1,
                                      bgcolor: enabled
                                          ? theme.palette.secondary.light
                                          : theme.palette.grey[100],
                                      borderRadius: "30px",
                                      px: "0.9rem",
                                      py: "0.55rem",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 2,
                                      minHeight: "2.5rem",
                                  }}
                              >
                                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                                      <Typography sx={{ color: "grey.600", textAlign: "left" }}>
                                          VID {i + 1}
                                      </Typography>

                                      <Typography sx={{ color: "grey.600" }}>
                                          {vid.startsWith("VID:") ? vid.slice(4) : vid}
                                      </Typography>
                                  </Box>

                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Typography sx={{ fontWeight: 500, color: "#003936" }}>
                                          Autocharge
                                      </Typography>

                                      <Switch
                                          checked={enabled ?? true}
                                          disabled={lockAutochargeEdition || isNewVid}
                                          onChange={() => {
                                              setConfirmedVids((prev) =>
                                                  prev.map((item) =>
                                                      item.vid === vid
                                                          ? {
                                                              ...item,
                                                              enabled: !(item.enabled ?? true),
                                                          }
                                                          : item
                                                  )
                                              )
                                          }}
                                          sx={{
                                              width: 50,
                                              height: 26,
                                              padding: 0,
                                              pointerEvents: isNewVid ? "none" : "auto",

                                              "& .MuiSwitch-switchBase": {
                                                  padding: "2px",
                                                  "&.Mui-checked": {
                                                      transform: "translateX(24px)",
                                                      color: "#fff",
                                                      "& + .MuiSwitch-track": {
                                                          backgroundColor: theme.palette.secondary.main,
                                                          opacity: 1,
                                                      },
                                                  },
                                              },
                                              "& .MuiSwitch-thumb": {
                                                  width: 22,
                                                  height: 22,
                                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                              },
                                              "& .MuiSwitch-track": {
                                                  borderRadius: 13,
                                                  backgroundColor: theme.palette.grey[500],
                                                  opacity: 1,
                                              },
                                          }}
                                      />
                                  </Box>
                              </Box>
                              <Box
                                  sx={{
                                      width: 32,
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      flexShrink: 0,
                                  }}
                              >
                                  {showClearButton && (
                                  <IconButton
                                      size="small"
                                      onClick={() => {
                                          setConfirmedVids((prev) =>
                                              prev.filter((item) => item.vid !== vid)
                                          )
                                      }}
                                  >
                                      <ClearIcon fontSize="small" />
                                  </IconButton>
                              )}
                              </Box>
                          </Box>
                      )})}
              </Stack>

              {confirmedCount < max && !lockAutochargeEdition && (<Box
                  sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: "0.75rem",
                      alignItems: { xs: "stretch", sm: "flex-start" },
                      mt: 0,
                      width: "100%",
                  }}
              >
                  <Box sx={{ flex: 1 }}>
                      <Controller
                          name={uidName as unknown as FieldPath<FormT>}
                          control={control}
                          render={({ field }) => (
                              <StyledTextField
                                  {...field}
                                  id={`vehicle-vid-${index}`}
                                  label="VID"
                                  disabled={confirmedCount >= max}
                                  bgcolor={theme.palette.grey[100]}
                                  error={!!vidsErrors?.vids?.[index]?.uid}
                                  // slotProps={{
                                  //     htmlInput: {
                                  //         maxLength: 12,
                                  //         pattern: "[A-Z0-9]{0,12}",
                                  //     },
                                  // }}
                                  helperText={
                                      vidsErrors?.vids?.[index]?.uid?.message ||
                                      ""
                                  }
                                  onPaste={(e) => {
                                      e.preventDefault()
                                  }}
                                  onKeyDown={(e) => {
                                      const key = (e.key || "").toLowerCase()
                                      if (
                                          (e.ctrlKey || e.metaKey) &&
                                          key === "v"
                                      ) {
                                          e.preventDefault()
                                      }
                                  }}
                                  onChange={(e) => {
                                      const value = e.target.value.toUpperCase()
                                      e.target.value = value
                                      field.onChange(e)
                                  }}
                              />
                          )}
                      />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                      <Controller
                          name={uidConfirmName as unknown as FieldPath<FormT>}
                          control={control}
                          render={({ field }) => (
                              <StyledTextField
                                  {...field}
                                  id={`vehicle-vid-confirm-${index}`}
                                  label="Confirmation du VID"
                                  disabled={confirmedCount >= max}
                                  bgcolor={theme.palette.grey[100]}
                                  error={
                                      !!vidsErrors?.vids?.[index]?.uidConfirm
                                  }
                                  helperText={
                                      vidsErrors?.vids?.[index]?.uidConfirm
                                          ? vidsErrors?.vids?.[index]
                                                ?.uidConfirm?.message ||
                                            "Les deux champs doivent être identiques"
                                          : ""
                                  }
                                  onPaste={(e) => {
                                      e.preventDefault()
                                  }}
                                  onKeyDown={(e) => {
                                      const key = (e.key || "").toLowerCase()
                                      if (
                                          (e.ctrlKey || e.metaKey) &&
                                          key === "v"
                                      ) {
                                          e.preventDefault()
                                      }
                                  }}
                                  onChange={(e) => {
                                      const value = e.target.value.toUpperCase()
                                      e.target.value = value
                                      field.onChange(e)
                                  }}
                              />
                          )}
                      />
                  </Box>
                  <OreveButton
                      sx={{ height: "fit-content", whiteSpace: "nowrap", border:"1px solid", px: 2, top: "10px",
                          "& .MuiButton-startIcon": {
                              ml: 0,
                              mr: -1,
                          }
                      }}
                      disabled={confirmedCount === max}
                      type="button"
                      variant="outlined"
                      value="Ajouter le VID"
                      startIcon={<CheckCircleIcon fontSize="small" sx={{ fontSize: "23px !important" }} />}
                      onClick={() => {
                          if (confirmedCount >= max) return
                          type VidsFormShape = {
                              vids?: Array<{
                                  uid?: string
                                  uidConfirm?: string
                              }>
                          }
                          const v = (getValues() as unknown as VidsFormShape)
                              ?.vids?.[index]
                          const a = (v?.uid ?? "").trim()
                          const b = (v?.uidConfirm ?? "").trim()

                          if (a === "" || b === "") {
                              setError(
                                  uidConfirmName as unknown as FieldPath<FormT>,
                                  {
                                      type: "validate",
                                      message:
                                          "Veuillez remplir les deux champs",
                                  }
                              )
                              return
                          }
                          if (a !== b) {
                              setError(
                                  uidConfirmName as unknown as FieldPath<FormT>,
                                  {
                                      type: "validate",
                                      message:
                                          "Les deux champs doivent être identiques",
                                  }
                              )
                              return
                          }
                          const already = Object.keys(confirmedVids).includes(a)
                          if (already) {
                              setError(
                                  uidConfirmName as unknown as FieldPath<FormT>,
                                  {
                                      type: "validate",
                                      message: "Ce VID est déjà confirmé",
                                  }
                              )
                              return
                          }
                          setConfirmedVids((prev) => [...prev, { vid: a, enabled: true }])
                          const uidPath = uidName as unknown as FieldPath<FormT>
                          const uidConfirmPath =
                              uidConfirmName as unknown as FieldPath<FormT>
                          setValue(
                              uidPath,
                              "" as unknown as PathValue<
                                  FormT,
                                  FieldPath<FormT>
                              >
                          )
                          setValue(
                              uidConfirmPath,
                              "" as unknown as PathValue<
                                  FormT,
                                  FieldPath<FormT>
                              >
                          )
                          if (!/^[A-Z0-9]{1,12}$/.test(a)) {
                              setError(uidName as unknown as FieldPath<FormT>, {
                                  type: "validate",
                                  message: "Le VID doit contenir uniquement A-Z et 0-9 (max 12 caractères)",
                              })
                              return
                          }
                          clearErrors([
                              uidPath,
                              uidConfirmPath,
                          ] as FieldPath<FormT>[])
                      }}
                  />
              </Box>)}
          </Box>
      </Box>
  )
}